import React, { useState, useEffect } from 'react';
import axios from 'axios';
import yaml from 'js-yaml';
import {
  Box, Card, CardContent, Button, ButtonGroup, MenuItem, TextField,
  Chip, List, ListItem, Alert, AlertTitle, CircularProgress, Stack, Typography
} from '@mui/material';

const UNIFORM_OPTIONS = [
  { label: 'Sports Uniform', value: 'Sports Uniform', image: '/images/sports-uniform.png' },
  { label: 'Formal Uniform', value: 'Formal Uniform', image: '/images/formal-uniform.png' },
  { label: 'House Uniform', value: 'House Uniform', image: '/images/house-uniform.png' },
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
const ALLOWED_GITHUB_USERS = ['rujiali'];

const dayLabel = (d) => d.charAt(0).toUpperCase() + d.slice(1, 3);

const Admin = () => {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [activeWeek, setActiveWeek] = useState('weekA');
  const [activeDay, setActiveDay] = useState('monday');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const [scheduleRes, authRes] = await Promise.all([
          axios.get('/api/get-schedule').catch(() => axios.get('/data/schedule.yml')),
          axios.get('/.auth/me').catch(() => ({ data: { clientPrincipal: null } })),
        ]);
        setSchedule(yaml.load(scheduleRes.data));
        setUser(authRes.data?.clientPrincipal ?? null);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const dayData = schedule?.[activeWeek]?.[activeDay] ?? { uniform: '', uniformImage: '', schedule: [] };

  const updateUniform = (value) => {
    const opt = UNIFORM_OPTIONS.find(o => o.value === value);
    setSchedule(prev => ({
      ...prev,
      [activeWeek]: {
        ...prev[activeWeek],
        [activeDay]: {
          ...prev[activeWeek][activeDay],
          uniform: value,
          uniformImage: opt?.image ?? '',
        }
      }
    }));
  };

  const updateRow = (index, field, value) => {
    const rows = [...dayData.schedule];
    rows[index] = { ...rows[index], [field]: value };
    setSchedule(prev => ({
      ...prev,
      [activeWeek]: {
        ...prev[activeWeek],
        [activeDay]: { ...prev[activeWeek][activeDay], schedule: rows }
      }
    }));
  };

  const addRow = () => {
    const rows = [...dayData.schedule, { time: '', activity: '' }];
    setSchedule(prev => ({
      ...prev,
      [activeWeek]: {
        ...prev[activeWeek],
        [activeDay]: { ...prev[activeWeek][activeDay], schedule: rows }
      }
    }));
  };

  const removeRow = (index) => {
    const rows = dayData.schedule.filter((_, i) => i !== index);
    setSchedule(prev => ({
      ...prev,
      [activeWeek]: {
        ...prev[activeWeek],
        [activeDay]: { ...prev[activeWeek][activeDay], schedule: rows }
      }
    }));
  };

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState('');
  const [parseSuccess, setParseSuccess] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    setParseError('');
    setParseSuccess(false);
    try {
      const buffer = await file.arrayBuffer();
      const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
      const res = await axios.post('/api/parse-schedule', {
        imageBase64: base64,
        mediaType: file.type,
      });
      const parsed = res.data;
      setSchedule(prev => ({
        ...prev,
        [activeWeek]: {
          ...prev[activeWeek],
          [activeDay]: {
            ...prev[activeWeek][activeDay],
            uniform: parsed.uniform || prev[activeWeek][activeDay].uniform,
            schedule: parsed.schedule || [],
          },
        },
      }));
      setParseSuccess(true);
      setTimeout(() => setParseSuccess(false), 5000);
    } catch (err) {
      setParseError(err.response?.data || 'Could not read the image — try again');
    } finally {
      setParsing(false);
      e.target.value = '';
    }
  };

  const saveSchedule = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const yamlStr = yaml.dump(schedule, { lineWidth: -1 });
      await axios.post('/api/save-schedule', yamlStr, {
        headers: { 'Content-Type': 'text/yaml' },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
    } catch (err) {
      setSaveError(err.response?.status === 401 ? 'Not authorised' : 'Save failed — try again');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isAuthorized = user && ALLOWED_GITHUB_USERS.includes(user.userDetails?.toLowerCase());

  if (user && !isAuthorized) {
    return (
      <Box sx={{ maxWidth: 500, mx: 'auto', mt: 5 }}>
        <Alert severity="error">
          <AlertTitle>Not authorized</AlertTitle>
          <Typography sx={{ mb: 1 }}>Signed in as <strong>{user.userDetails}</strong>, but this account isn't allowed to manage the schedule.</Typography>
          <Button color="error" size="small" href="/.auth/logout">Sign out</Button>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', mt: 4 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6">Schedule Admin</Typography>
          {user && (
            <Typography variant="body2" color="text.secondary">
              Signed in as <strong>{user.userDetails}</strong>
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" color="inherit" size="small" href="/">
            ← View App
          </Button>
          {user && (
            <Button variant="outlined" color="error" size="small" href="/.auth/logout">
              Sign out
            </Button>
          )}
        </Stack>
      </Stack>

      {saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Schedule saved — changes are live immediately.
        </Alert>
      )}
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>
      )}

      {!user && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Running locally — authentication not active.
        </Alert>
      )}

      <Card>
        <CardContent sx={{ pb: 1 }}>
          {/* Week selector */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <ButtonGroup size="small">
              <Button
                variant={activeWeek === 'weekA' ? 'contained' : 'outlined'}
                onClick={() => setActiveWeek('weekA')}
              >
                Week A
              </Button>
              <Button
                variant={activeWeek === 'weekB' ? 'contained' : 'outlined'}
                onClick={() => setActiveWeek('weekB')}
              >
                Week B
              </Button>
            </ButtonGroup>
            <Button variant="contained" color="success" size="small" onClick={saveSchedule} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Stack>

          {/* Day selector */}
          <ButtonGroup fullWidth size="small">
            {DAYS.map(d => (
              <Button
                key={d}
                variant={activeDay === d ? 'contained' : 'outlined'}
                onClick={() => setActiveDay(d)}
              >
                {dayLabel(d)}
              </Button>
            ))}
          </ButtonGroup>
        </CardContent>

        <CardContent>
          {/* Uniform */}
          <Stack direction="row" alignItems="center" spacing={2} mb={3}>
            <Typography fontWeight={600}>Uniform</Typography>
            <TextField
              select
              size="small"
              value={dayData.uniform}
              onChange={e => updateUniform(e.target.value)}
              sx={{ flex: 1 }}
            >
              {UNIFORM_OPTIONS.map(o => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
            <Chip label={dayData.uniform || '—'} color="info" size="small" />
          </Stack>

          {/* Import from photo */}
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <Button variant="outlined" color="secondary" size="small" component="label" disabled={parsing}>
              {parsing ? 'Reading…' : '📷 Import from photo'}
              <input hidden accept="image/*" type="file" onChange={handleImageUpload} />
            </Button>
            {parseSuccess && <Typography variant="caption" color="success.main">Pre-filled — review and save when ready</Typography>}
            {parseError && <Typography variant="caption" color="error.main">{parseError}</Typography>}
          </Stack>

          {/* Schedule rows */}
          <Typography variant="body2" fontWeight={600} mb={1}>Schedule</Typography>
          <List disablePadding sx={{ mb: 2 }}>
            {dayData.schedule.map((row, i) => (
              <ListItem key={i} divider sx={{ px: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" width="100%">
                  <TextField
                    size="small"
                    type="time"
                    value={row.time}
                    onChange={e => updateRow(i, 'time', e.target.value)}
                    sx={{ width: 130 }}
                  />
                  <TextField
                    size="small"
                    value={row.activity}
                    placeholder="Activity"
                    onChange={e => updateRow(i, 'activity', e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <Button color="error" variant="outlined" size="small" onClick={() => removeRow(i)}>
                    ✕
                  </Button>
                </Stack>
              </ListItem>
            ))}
          </List>

          <Button variant="outlined" color="inherit" size="small" onClick={addRow}>
            + Add row
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Admin;
